import { Class } from '../models/Class';
import { Roles } from '../models/User';

export interface ClassCardData {
  id: string;
  name: string;
  section: string;
  gradeLabel: string;
  studentCount: number;
  hasCoTeacher: boolean;
  isClever: boolean;
  ownerName: string;
  siteName: string;
  siteId: string;
  gradeSortValue: number;
}

// Grade names arrive as e.g. "4th Grade"; strip "Grade" and the ordinal suffix
// so we can render "Grade 4, 5" instead of "4th Grade, 5th Grade".
export function getGradeNumbers(gradeLevels: { name?: string | null }[] | undefined): string[] {
  return (gradeLevels ?? [])
    .map(
      grade =>
        grade.name
          ?.replace(/grade/gi, '')
          .replace(/(\d+)(st|nd|rd|th)/gi, '$1')
          .replace(/\s+/g, ' ')
          .trim(),
    )
    .filter((value): value is string => Boolean(value));
}

export function getGradeLabel(gradeLevels: { name?: string | null }[] | undefined): string {
  const gradeNumbers = getGradeNumbers(gradeLevels);
  return gradeNumbers.length ? `Grade ${gradeNumbers.join(', ')}` : '';
}

export function getClassCardData(cls: Class): ClassCardData {
  const users = cls.users ?? [];
  const hasRole = (user: (typeof users)[number], role: Roles) => user.roles?.some(r => r?.name === role);

  const studentCount = users.filter(user => hasRole(user, Roles.STUDENT)).length;
  const hasCoTeacher = users.some(user => hasRole(user, Roles.TEACHER) && user.id !== cls.owner?.id);

  const gradeNumbers = getGradeNumbers(cls.gradeLevels);
  const gradeLabel = getGradeLabel(cls.gradeLevels);

  const parsedGrades = gradeNumbers.map(value => parseInt(value, 10)).filter(value => !Number.isNaN(value));
  const gradeSortValue = parsedGrades.length ? Math.min(...parsedGrades) : Number.POSITIVE_INFINITY;

  const ownerName = `${cls.owner?.firstName ?? ''} ${cls.owner?.lastName ?? ''}`.trim();

  return {
    id: cls.id,
    name: cls.name,
    section: cls.section ?? '',
    gradeLabel,
    studentCount,
    hasCoTeacher,
    isClever: Boolean(cls.externalSync?.source),
    ownerName,
    siteName: cls.site?.name ?? '',
    siteId: cls.site?.id ?? '',
    gradeSortValue,
  };
}
